
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { 
  DropdownMenu, 
  DropdownMenuTrigger, 
  DropdownMenuContent, 
  DropdownMenuItem
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MoreHorizontal, CheckCircle, ExternalLink } from 'lucide-react';

interface Alert {
  id: string;
  severity: 'high' | 'medium' | 'low';
  message: string;
  age: string;
}

interface OpenAlertsProps {
  alerts: Alert[];
  onAlertClick?: (id: string) => void;
  onAcknowledgeAlert?: (id: string) => void;
  onCreateTicket?: (id: string) => void;
}

const OpenAlerts: React.FC<OpenAlertsProps> = ({ 
  alerts, 
  onAlertClick,
  onAcknowledgeAlert,
  onCreateTicket
}) => {
  const getSeverityClass = (severity: Alert['severity']) => {
    switch (severity) {
      case 'high':
        return 'bg-status-red/20 text-status-red';
      case 'medium':
        return 'bg-status-yellow/20 text-status-yellow';
      case 'low':
        return 'bg-accent-teal/20 text-accent-teal';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleAction = (e: React.MouseEvent, callback?: (id: string) => void, id?: string) => {
    e.stopPropagation();
    if (callback && id) {
      callback(id);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Open Alerts</CardTitle>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <a 
                href="/alerts" 
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                View all
              </a>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>View all alerts</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {alerts.map((alert) => (
            <li 
              key={alert.id}
              onClick={() => onAlertClick?.(alert.id)}
              className="border rounded-md p-3 hover:bg-muted/50 cursor-pointer transition-colors group"
            >
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-3 flex-grow">
                  <span className={cn(
                    "px-2 py-1 rounded-sm text-xs font-medium",
                    getSeverityClass(alert.severity)
                  )}>
                    {alert.severity.toUpperCase()}
                  </span>
                  <div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className="font-medium">{alert.message}</p>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p>Click to view alert details</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <span className="text-xs text-muted-foreground">
                    {alert.age}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-muted transition-opacity" onClick={(e) => e.stopPropagation()}>
                        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => handleAction(e, onAcknowledgeAlert, alert.id)}>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        <span>Acknowledge</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => handleAction(e, onCreateTicket, alert.id)}>
                        <ExternalLink className="mr-2 h-4 w-4" />
                        <span>Create Jira ticket</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export default OpenAlerts;
